/**
 * RD Shopify Core v1.1
 * Load once with `defer`.
 *
 * Responsibilities:
 * 1. Tiny idempotent component registry.
 * 2. Shopify Theme Editor section load/unload support.
 * 3. Shared native carousel controller.
 * 4. Lightweight PDP live sold counter.
 */

(() => {
  'use strict';

  if (window.RD && window.RD.version) return;

  const registry = new Map();
  const states = new WeakMap();

  const getState = (element) => {
    let state = states.get(element);

    if (!state) {
      state = new Map();
      states.set(element, state);
    }

    return state;
  };

  const findComponentElements = (root, name) => {
    const selector = `[data-rd-component="${name}"]`;
    const elements = [];

    if (
      root instanceof Element &&
      root.matches(selector)
    ) {
      elements.push(root);
    }

    if (root.querySelectorAll) {
      elements.push(
        ...root.querySelectorAll(selector)
      );
    }

    return elements;
  };

  const initComponent = (
    element,
    name,
    initializer
  ) => {
    const state = getState(element);

    if (state.has(name)) return;

    try {
      const cleanup =
        initializer(element) || null;

      state.set(name, {
        cleanup
      });
    } catch (error) {
      console.error(
        `[RD] Failed to initialize "${name}"`,
        error,
        element
      );
    }
  };

  const init = (root = document) => {
    registry.forEach(
      (initializer, name) => {
        findComponentElements(
          root,
          name
        ).forEach((element) => {
          initComponent(
            element,
            name,
            initializer
          );
        });
      }
    );

    initLiveSoldCounters(root);
  };

  const destroy = (root = document) => {
    registry.forEach((_, name) => {
      findComponentElements(
        root,
        name
      ).forEach((element) => {
        const state =
          states.get(element);

        const instance =
          state &&
          state.get(name);

        if (
          instance &&
          typeof instance.cleanup ===
            'function'
        ) {
          try {
            instance.cleanup();
          } catch (error) {
            console.error(
              `[RD] Failed to destroy "${name}"`,
              error,
              element
            );
          }
        }

        if (state) {
          state.delete(name);
        }
      });
    });

    destroyLiveSoldCounters(root);
  };

  const register = (
    name,
    initializer
  ) => {
    if (
      !name ||
      typeof initializer !==
        'function'
    ) {
      return;
    }

    registry.set(
      name,
      initializer
    );

    if (
      document.readyState !==
      'loading'
    ) {
      findComponentElements(
        document,
        name
      ).forEach((element) => {
        initComponent(
          element,
          name,
          initializer
        );
      });
    }
  };

  const emit = (
    name,
    detail = {},
    target = document
  ) => {
    target.dispatchEvent(
      new CustomEvent(
        `rd:${name}`,
        {
          detail,
          bubbles: true
        }
      )
    );
  };

  window.RD = {
    version: '1.2.0',
    register,
    init,
    destroy,
    emit
  };


  /* ==========================================================
     SHARED CAROUSEL

     Markup contract:

     <div
       class="rd-carousel"
       data-rd-component="carousel"
       data-autoplay="0"
       data-loop="false"
     >
       <div
         class="rd-carousel__viewport"
         data-rd-carousel-viewport
       >
         <div
           class="rd-carousel__track"
           data-rd-carousel-track
         >
           <div class="rd-carousel__slide">
             ...
           </div>
         </div>
       </div>

       <button data-rd-carousel-prev>
         ...
       </button>

       <button data-rd-carousel-next>
         ...
       </button>

       <div
         class="rd-carousel__dots"
         data-rd-carousel-dots
       ></div>
     </div>
     ========================================================== */

  register(
    'carousel',
    (root) => {
      const viewport =
        root.querySelector(
          '[data-rd-carousel-viewport]'
        );

      const track =
        root.querySelector(
          '[data-rd-carousel-track]'
        );

      const prev =
        root.querySelector(
          '[data-rd-carousel-prev]'
        );

      const next =
        root.querySelector(
          '[data-rd-carousel-next]'
        );

      const dotsHost =
        root.querySelector(
          '[data-rd-carousel-dots]'
        );

      const slides = track
        ? Array.from(
            track.children
          )
        : [];

      if (
        !viewport ||
        !track ||
        slides.length === 0
      ) {
        return;
      }

      const loop =
        root.dataset.loop ===
        'true';

      const autoplayMs =
        Math.max(
          0,
          Number(
            root.dataset.autoplay ||
            0
          )
        );

      const reducedMotion =
        window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches;

      const EDGE_TOLERANCE = 3;
      const STEP_TOLERANCE = 4;

      const allowsHorizontalScroll =
        (element) => {
          const overflowX =
            window.getComputedStyle(
              element
            ).overflowX;

          return (
            overflowX === 'auto' ||
            overflowX === 'scroll'
          );
        };

      /*
       * Most RD carousels scroll the viewport. A few older sections
       * made the track itself scrollable. Detect the real scroller so
       * the same prev/next controller works reliably in both layouts.
       */
      const scroller =
        allowsHorizontalScroll(viewport)
          ? viewport
          : allowsHorizontalScroll(track)
            ? track
            : viewport;

      let activeIndex = 0;
      let autoplayTimer = 0;
      let visible = true;
      let destroyed = false;
      let scrollRaf = 0;
      let resizeRaf = 0;

      const clamp = (
        value,
        min,
        max
      ) => {
        return Math.min(
          Math.max(
            value,
            min
          ),
          max
        );
      };

      const maxScroll = () => {
        return Math.max(
          0,
          scroller.scrollWidth -
            scroller.clientWidth
        );
      };

      const canScroll = () => {
        return (
          maxScroll() >
          EDGE_TOLERANCE
        );
      };

      const slideLeft = (
        index
      ) => {
        const slide = slides[index];

        if (!slide) return 0;

        const scrollerRect =
          scroller.getBoundingClientRect();

        const slideRect =
          slide.getBoundingClientRect();

        return clamp(
          scroller.scrollLeft +
            slideRect.left -
            scrollerRect.left,
          0,
          maxScroll()
        );
      };

      const nearestIndex =
        () => {
          const left =
            scroller.scrollLeft;

          let nearest = 0;
          let distance = Infinity;

          slides.forEach(
            (_, index) => {
              const current =
                Math.abs(
                  slideLeft(index) -
                    left
                );

              if (
                current < distance
              ) {
                distance = current;
                nearest = index;
              }
            }
          );

          return nearest;
        };

      const isAtStart = () => {
        return (
          scroller.scrollLeft <=
          EDGE_TOLERANCE
        );
      };

      const isAtEnd = () => {
        return (
          scroller.scrollLeft >=
          maxScroll() -
            EDGE_TOLERANCE
        );
      };

      const updateControls =
        () => {
          activeIndex =
            nearestIndex();

          const scrollable =
            canScroll();

          const atStart =
            isAtStart();

          const atEnd =
            isAtEnd();

          root.dataset.canScroll =
            String(scrollable);

          root.dataset.atStart =
            String(atStart);

          root.dataset.atEnd =
            String(atEnd);

          if (prev) {
            prev.disabled =
              !scrollable ||
              (!loop && atStart);

            prev.setAttribute(
              'aria-disabled',
              String(
                prev.disabled
              )
            );
          }

          if (next) {
            next.disabled =
              !scrollable ||
              (!loop && atEnd);

            next.setAttribute(
              'aria-disabled',
              String(
                next.disabled
              )
            );
          }

          if (dotsHost) {
            dotsHost
              .querySelectorAll(
                '[data-rd-carousel-dot]'
              )
              .forEach(
                (
                  dot,
                  index
                ) => {
                  const current =
                    index ===
                    activeIndex;

                  dot.setAttribute(
                    'aria-current',
                    String(current)
                  );
                }
              );
          }

          root.dataset.activeIndex =
            String(activeIndex);

          emit(
            'carousel:change',
            {
              index: activeIndex,
              carousel: root
            },
            root
          );
        };

      const goTo = (
        requestedIndex,
        behavior =
          reducedMotion
            ? 'auto'
            : 'smooth'
      ) => {
        let index =
          requestedIndex;

        if (loop) {
          index =
            (
              index %
                slides.length +
              slides.length
            ) %
            slides.length;
        } else {
          index = clamp(
            index,
            0,
            slides.length - 1
          );
        }

        scroller.scrollTo({
          left: slideLeft(index),
          behavior
        });
      };

      const previousIndex =
        () => {
          const currentLeft =
            scroller.scrollLeft;

          for (
            let index =
              slides.length - 1;
            index >= 0;
            index -= 1
          ) {
            if (
              slideLeft(index) <
              currentLeft -
                STEP_TOLERANCE
            ) {
              return index;
            }
          }

          return 0;
        };

      const nextIndex = () => {
        const currentLeft =
          scroller.scrollLeft;

        for (
          let index = 0;
          index < slides.length;
          index += 1
        ) {
          if (
            slideLeft(index) >
            currentLeft +
              STEP_TOLERANCE
          ) {
            return index;
          }
        }

        return (
          slides.length - 1
        );
      };

      const previous = () => {
        updateControls();

        if (!canScroll()) return;

        if (
          loop &&
          isAtStart()
        ) {
          goTo(
            slides.length - 1
          );
          return;
        }

        goTo(
          previousIndex()
        );
      };

      const following = () => {
        updateControls();

        if (!canScroll()) return;

        if (
          loop &&
          isAtEnd()
        ) {
          goTo(0);
          return;
        }

        goTo(
          nextIndex()
        );
      };

      const onPrev =
        (event) => {
          event.preventDefault();
          previous();
        };

      const onNext =
        (event) => {
          event.preventDefault();
          following();
        };

      const onScroll = () => {
        cancelAnimationFrame(
          scrollRaf
        );

        scrollRaf =
          requestAnimationFrame(
            updateControls
          );
      };

      if (prev) {
        prev.addEventListener(
          'click',
          onPrev
        );
      }

      if (next) {
        next.addEventListener(
          'click',
          onNext
        );
      }

      scroller.addEventListener(
        'scroll',
        onScroll,
        {
          passive: true
        }
      );


      /* Dots are optional. */

      if (dotsHost) {
        const fragment =
          document
            .createDocumentFragment();

        slides.forEach(
          (_, index) => {
            const dot =
              document.createElement(
                'button'
              );

            dot.type = 'button';

            dot.className =
              'rd-carousel__dot';

            dot.dataset
              .rdCarouselDot =
              String(index);

            dot.setAttribute(
              'aria-label',
              `Go to slide ${
                index + 1
              }`
            );

            dot.addEventListener(
              'click',
              () => goTo(index)
            );

            fragment
              .appendChild(dot);
          }
        );

        dotsHost
          .replaceChildren(
            fragment
          );
      }


      /* Responsive state. */

      const queueResizeUpdate =
        () => {
          cancelAnimationFrame(
            resizeRaf
          );

          resizeRaf =
            requestAnimationFrame(
              updateControls
            );
        };

      const resizeObserver =
        'ResizeObserver' in window
          ? new ResizeObserver(
              queueResizeUpdate
            )
          : null;

      if (resizeObserver) {
        resizeObserver.observe(
          scroller
        );

        if (
          track !== scroller
        ) {
          resizeObserver.observe(
            track
          );
        }
      } else {
        window.addEventListener(
          'resize',
          queueResizeUpdate,
          {
            passive: true
          }
        );
      }


      /* Pause autoplay off-screen. */

      const intersectionObserver =
        autoplayMs > 0 &&
        'IntersectionObserver' in window
          ? new IntersectionObserver(
              (entries) => {
                visible = Boolean(
                  entries[0] &&
                    entries[0]
                      .isIntersecting
                );
              },
              {
                threshold: 0.15
              }
            )
          : null;

      if (intersectionObserver) {
        intersectionObserver.observe(
          root
        );
      }

      const startAutoplay = () => {
        if (
          !autoplayMs ||
          reducedMotion ||
          autoplayTimer ||
          !canScroll()
        ) {
          return;
        }

        autoplayTimer =
          window.setInterval(
            () => {
              if (
                !visible ||
                document.hidden ||
                destroyed
              ) {
                return;
              }

              updateControls();

              if (
                !loop &&
                isAtEnd()
              ) {
                goTo(0);
                return;
              }

              following();
            },
            autoplayMs
          );
      };

      const stopAutoplay = () => {
        if (!autoplayTimer) {
          return;
        }

        clearInterval(
          autoplayTimer
        );

        autoplayTimer = 0;
      };

      const onPointerEnter =
        () => {
          stopAutoplay();
        };

      const onPointerLeave =
        () => {
          startAutoplay();
        };

      const onFocusIn =
        () => {
          stopAutoplay();
        };

      const onFocusOut =
        (event) => {
          if (
            !root.contains(
              event.relatedTarget
            )
          ) {
            startAutoplay();
          }
        };

      const onVisibilityChange =
        () => {
          if (document.hidden) {
            stopAutoplay();
          } else {
            startAutoplay();
          }
        };

      if (autoplayMs > 0) {
        root.addEventListener(
          'pointerenter',
          onPointerEnter
        );

        root.addEventListener(
          'pointerleave',
          onPointerLeave
        );

        root.addEventListener(
          'focusin',
          onFocusIn
        );

        root.addEventListener(
          'focusout',
          onFocusOut
        );

        document.addEventListener(
          'visibilitychange',
          onVisibilityChange
        );

        startAutoplay();
      }

      updateControls();

      return () => {
        destroyed = true;

        stopAutoplay();

        cancelAnimationFrame(
          scrollRaf
        );

        cancelAnimationFrame(
          resizeRaf
        );

        if (prev) {
          prev.removeEventListener(
            'click',
            onPrev
          );
        }

        if (next) {
          next.removeEventListener(
            'click',
            onNext
          );
        }

        scroller.removeEventListener(
          'scroll',
          onScroll
        );

        root.removeEventListener(
          'pointerenter',
          onPointerEnter
        );

        root.removeEventListener(
          'pointerleave',
          onPointerLeave
        );

        root.removeEventListener(
          'focusin',
          onFocusIn
        );

        root.removeEventListener(
          'focusout',
          onFocusOut
        );

        document.removeEventListener(
          'visibilitychange',
          onVisibilityChange
        );

        if (resizeObserver) {
          resizeObserver.disconnect();
        } else {
          window.removeEventListener(
            'resize',
            queueResizeUpdate
          );
        }

        if (intersectionObserver) {
          intersectionObserver
            .disconnect();
        }
      };
    }
  );

  /* ==========================================================
     PDP LIVE SOLD COUNTER

     Required markup:

     data-rd-sold-live
     data-product-id="{{ product.id }}"
     data-min="1"
     data-max="8"

     Child:
     data-rd-sold-count

     Behavior:
     - Product-specific number.
     - Strict range: 1 to 8.
     - Stable for each 15-minute window.
     - Changes on the next 15-minute window.
     - Refresh does not randomize it.
     - Lightweight 30-second boundary check.
     - Theme Editor safe.
     ========================================================== */

  const SOLD_MIN = 1;
  const SOLD_MAX = 8;

  const SOLD_BUCKET_MS =
    15 * 60 * 1000;

  const SOLD_CHECK_MS =
    30 * 1000;

  const soldStates =
    new WeakMap();

  const getLiveSoldElements = (
    root = document
  ) => {
    const elements = [];

    if (
      root instanceof Element &&
      root.matches(
        '[data-rd-sold-live]'
      )
    ) {
      elements.push(root);
    }

    if (
      root.querySelectorAll
    ) {
      elements.push(
        ...root.querySelectorAll(
          '[data-rd-sold-live]'
        )
      );
    }

    return elements;
  };

  const createSoldSeed = (
    productId,
    bucket
  ) => {
    let seed =
      (
        productId ^
        Math.imul(
          bucket,
          2654435761
        )
      ) >>> 0;

    seed =
      Math.imul(
        seed ^
        (seed >>> 16),
        2246822507
      );

    seed =
      Math.imul(
        seed ^
        (seed >>> 13),
        3266489909
      );

    return (
      seed ^
      (seed >>> 16)
    ) >>> 0;
  };

  const getSoldNumber = (
    productId,
    min,
    max,
    bucket
  ) => {
    if (
      max <= min
    ) {
      return max;
    }

    const seed =
      createSoldSeed(
        productId,
        bucket
      );

    const random =
      seed /
      4294967295;

    return (
      Math.floor(
        random *
        (
          max -
          min +
          1
        )
      ) +
      min
    );
  };

  const initLiveSoldCounter = (
    element
  ) => {
    if (
      !element ||
      soldStates.has(
        element
      )
    ) {
      return;
    }

    const output =
      element.querySelector(
        '[data-rd-sold-count]'
      );

    if (!output) return;

    const productId =
      parseInt(
        element.dataset
          .productId,
        10
      ) || 1;


    /* Strictly clamp range to 1–8. */

    const requestedMin =
      parseInt(
        element.dataset.min,
        10
      );

    const requestedMax =
      parseInt(
        element.dataset.max,
        10
      );

    let min =
      Number.isFinite(
        requestedMin
      )
        ? Math.min(
            SOLD_MAX,
            Math.max(
              SOLD_MIN,
              requestedMin
            )
          )
        : SOLD_MIN;

    let max =
      Number.isFinite(
        requestedMax
      )
        ? Math.min(
            SOLD_MAX,
            Math.max(
              SOLD_MIN,
              requestedMax
            )
          )
        : SOLD_MAX;


    /* Invalid/reversed range falls back to 1–8. */

    if (
      min > max
    ) {
      min = SOLD_MIN;
      max = SOLD_MAX;
    }

    let activeBucket = -1;
    let timer = 0;

    const update = () => {
      const bucket =
        Math.floor(
          Date.now() /
          SOLD_BUCKET_MS
        );

      if (
        bucket ===
        activeBucket
      ) {
        return;
      }

      activeBucket =
        bucket;

      const value =
        getSoldNumber(
          productId,
          min,
          max,
          bucket
        );

      output.textContent =
        String(value);
    };

    update();

    timer =
      window.setInterval(
        update,
        SOLD_CHECK_MS
      );

    soldStates.set(
      element,
      {
        timer
      }
    );
  };

  const initLiveSoldCounters = (
    root = document
  ) => {
    getLiveSoldElements(
      root
    ).forEach(
      initLiveSoldCounter
    );
  };

  const destroyLiveSoldCounters = (
    root = document
  ) => {
    getLiveSoldElements(
      root
    ).forEach(
      (element) => {
        const state =
          soldStates.get(
            element
          );

        if (!state) return;

        if (
          state.timer
        ) {
          window.clearInterval(
            state.timer
          );
        }

        soldStates.delete(
          element
        );
      }
    );
  };


  /* ==========================================================
     BOOT
     ========================================================== */

  const boot = () => {
    init(document);
  };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      boot,
      {
        once: true
      }
    );
  } else {
    boot();
  }


  /* Shopify Theme Editor lifecycle. */

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      init(
        event.target
      );
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      destroy(
        event.target
      );
    }
  );
})();